<template>
  <section class="dashboard">
    <div class="page-header">
      <h3 class="page-title">Dashboard</h3>
    </div>

    <div v-if="loading" class="loading-container">
      <i class="fa fa-cog fa-spin"></i>
      <span>Daten werden geladen...</span>
    </div>

    <template v-if="!loading">
      <!-- KPI Cards -->
      <div class="row kpi-row">
        <div class="col grid-margin">
          <div class="card kpi-card">
            <div class="card-body">
              <div class="kpi-icon" style="background-color: #198ae3;">
                <i class="fa fa-users"></i>
              </div>
              <div class="kpi-value">{{ customerCount }}</div>
              <div class="kpi-label">Aktive Kunden</div>
            </div>
          </div>
        </div>
        <div class="col grid-margin">
          <div class="card kpi-card">
            <div class="card-body">
              <div class="kpi-icon" style="background-color: #f0a030;">
                <i class="fa fa-flask"></i>
              </div>
              <div class="kpi-value">{{ testCount }}</div>
              <div class="kpi-label">Test-Accounts</div>
            </div>
          </div>
        </div>
        <div class="col grid-margin">
          <div class="card kpi-card">
            <div class="card-body">
              <div class="kpi-icon" style="background-color: #e34a19;">
                <i class="fa fa-file-text-o"></i>
              </div>
              <div class="kpi-value">{{ openBillingsCount === null ? '–' : openBillingsCount }}</div>
              <div class="kpi-label">Offene Rechnungen</div>
              <div class="kpi-sub" v-if="openBillingsSum !== null">{{ openBillingsSum | toCurrency }} netto</div>
            </div>
          </div>
        </div>
        <div class="col grid-margin">
          <div class="card kpi-card">
            <div class="card-body">
              <div class="kpi-icon" style="background-color: #e3194a;">
                <i class="fa fa-calendar-times-o"></i>
              </div>
              <div class="kpi-value">{{ dueBillings.length }}</div>
              <div class="kpi-label">Fällige Abrechnungen</div>
              <div class="kpi-sub" v-if="unclearSettlements.length > 0">+ {{ unclearSettlements.length }} mit unklarem Datum</div>
            </div>
          </div>
        </div>
        <div class="col grid-margin">
          <div class="card kpi-card">
            <div class="card-body">
              <div class="kpi-icon" style="background-color: #8e44ad;">
                <i class="fa fa-credit-card"></i>
              </div>
              <div class="kpi-value">{{ customersWithoutSepa.length }}</div>
              <div class="kpi-label">Ohne SEPA</div>
            </div>
          </div>
        </div>
        <div class="col grid-margin">
          <div class="card kpi-card">
            <div class="card-body">
              <div class="kpi-icon" style="background-color: #16a085;">
                <i class="fa fa-mobile"></i>
              </div>
              <div class="kpi-value">{{ flutterCount }}</div>
              <div class="kpi-label">Flutter-App im Einsatz</div>
              <div class="kpi-sub">von {{ locations.length }} Einrichtungen ({{ flutterShare }} %)</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Detail Tables -->
      <div class="row">
        <!-- Expiring test phases -->
        <div class="col-lg-6 grid-margin">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Ablaufende Testphasen <span class="badge-count">{{ expiringTests.length }}</span></h4>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Einrichtung</th>
                      <th>Kategorie</th>
                      <th>Test endet am</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in expiringTests" :key="item.uid">
                      <td>
                        <a class="link-col" :href="generateLink(item.uid)">{{ item.name }}</a>
                      </td>
                      <td>{{ item.category }}</td>
                      <td>{{ item.test_until }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="expiringTests.length === 0" class="empty-state">
                  <p>Keine ablaufenden Testphasen.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Due settlements -->
        <div class="col-lg-6 grid-margin">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Fällige Abrechnungen <span class="badge-count">{{ dueBillings.length }}</span></h4>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Einrichtung</th>
                      <th>Kategorie</th>
                      <th>Fällig seit</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in dueBillings" :key="item.uid">
                      <td>
                        <a class="link-col" :href="generateLink(item.uid)">{{ item.name }}</a>
                      </td>
                      <td>{{ item.category }}</td>
                      <td>{{ item.next_settlement }}</td>
                    </tr>
                  </tbody>
                </table>
                <div v-if="dueBillings.length === 0" class="empty-state">
                  <p>Keine fälligen Abrechnungen.</p>
                </div>
              </div>

              <!-- Unklares Datum: nicht weglassen, sondern zeigen. Der Server
                   liest z.B. 01.02.26 als faellig, die Liste als unbekannt. -->
              <template v-if="unclearSettlements.length > 0">
                <h5 class="unclear-title">Datum unklar <span class="badge-count badge-count-warn">{{ unclearSettlements.length }}</span></h5>
                <p class="unclear-hint">
                  Diese Kunden haben kein eindeutiges Datum für die nächste Abrechnung.
                  Bitte das Datum in der Einrichtung prüfen und korrigieren.
                </p>
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead>
                      <tr>
                        <th>Einrichtung</th>
                        <th>Kategorie</th>
                        <th>Eingetragenes Datum</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="item in unclearSettlements" :key="item.uid">
                        <td>
                          <a class="link-col" :href="generateLink(item.uid)">{{ item.name }}</a>
                        </td>
                        <td>{{ item.category }}</td>
                        <td>{{ item.next_settlement && item.next_settlement !== '-' ? item.next_settlement : 'keins' }}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>

      <!-- Customers without SEPA -->
      <div class="row" v-if="customersWithoutSepa.length > 0">
        <div class="col-12 grid-margin">
          <div class="card">
            <div class="card-body">
              <h4 class="card-title">Kunden ohne gesetztes Kennzeichen „SEPA-Lastschriftmandat“ <span class="badge-count badge-count-warn">{{ customersWithoutSepa.length }}</span></h4>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                    <tr>
                      <th>Einrichtung</th>
                      <th>Kategorie</th>
                      <th>Nächste Abrechnung</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in customersWithoutSepa" :key="item.uid">
                      <td>
                        <a class="link-col" :href="generateLink(item.uid)">{{ item.name }}</a>
                      </td>
                      <td>{{ item.category }}</td>
                      <td>{{ item.next_settlement || '–' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </section>
</template>

<script>
import { getAll } from "@/services/locations.service";
import { getAllBillings } from "@/services/billings.service";
import { settlementState } from "@/utils/settlement";

export default {
  name: "dashboard",
  data() {
    return {
      loading: true,
      // Dieselbe Quelle wie die Kopfzeile der Rechnungen-Seite:
      // open_count / open_sum aus GET /admin/locations/billings
      // (paid != true, nicht entfernt; Summe netto nach Rabatt, in Cent).
      // null = nicht geladen, die Kachel zeigt dann "–" statt einer falschen 0.
      openCountRaw: null,
      openSumCents: null,
    };
  },
  computed: {
    locations: function () {
      return this.$store.state.locations || [];
    },
    customerCount: function () {
      return this.locations.filter(function (l) {
        return l.account_state == "customer";
      }).length;
    },
    testCount: function () {
      return this.locations.filter(function (l) {
        return l.account_state == "test";
      }).length;
    },
    openBillingsCount: function () {
      return this.openCountRaw;
    },
    openBillingsSum: function () {
      return this.openSumCents === null ? null : this.openSumCents / 100;
    },
    // Dieselbe Menge wie die Arbeitsliste "Heute abzurechnen".
    dueBillings: function () {
      return this.locations.filter(function (l) {
        return l.account_state == "customer" && settlementState(l) === 'ja';
      });
    },
    // Was settlementState "unbekannt" nennt, ist nicht "nicht faellig" und wird
    // deshalb eigens gezeigt, dieselbe Menge wie die Arbeitsliste "Ohne
    // verwertbares Datum".
    unclearSettlements: function () {
      return this.locations.filter(function (l) {
        return l.account_state == "customer" && settlementState(l) === 'unbekannt';
      });
    },
    customersWithoutSepa: function () {
      return this.locations.filter(function (l) {
        return l.account_state == "customer" && l.billing_sepa != "y";
      });
    },
    flutterCount: function () {
      return this.locations.filter(function (l) {
        return l.flutter_app_first_seen_at;
      }).length;
    },
    flutterShare: function () {
      return this.locations.length
        ? Math.round((this.flutterCount / this.locations.length) * 100)
        : 0;
    },
    expiringTests: function () {
      var today = new Date();
      today.setHours(0, 0, 0, 0);

      var in14Days = new Date(today);
      in14Days.setDate(in14Days.getDate() + 14);

      return this.locations.filter(function (l) {
        if (l.account_state != "test") return false;
        if (!l.test_until) return false;

        var parts = l.test_until.split(".");
        if (parts.length !== 3) return false;

        var testDate = new Date(parts[2], parts[1] - 1, parts[0]);
        testDate.setHours(0, 0, 0, 0);

        return testDate >= today && testDate <= in14Days;
      });
    },
  },
  methods: {
    generateLink: function (uid) {
      return this.$router.resolve({
        name: "location-detail",
        params: { uid: uid },
      }).href;
    },
  },
  mounted() {
    var self = this;
    var locationsLoaded = false;
    var billingsLoaded = false;

    function checkDone() {
      if (locationsLoaded && billingsLoaded) {
        self.loading = false;
      }
    }

    getAll()
      .then(function (resp) {
        if (resp.data.error) {
          localStorage.removeItem("token");
          self.$router.push({ name: "login" });
        } else {
          self.$store.state.locations = resp.data.locations;
        }
        locationsLoaded = true;
        checkDone();
      })
      .catch(function () {
        localStorage.removeItem("token");
        self.$router.push({ name: "login" });
      });

    // limit 1: nur die Kopfzahlen werden gebraucht, nicht die Liste
    // (limit 0 wuerde der Server als 1000 lesen).
    getAllBillings(1, 0)
      .then(function (resp) {
        if (
          resp.data &&
          !resp.data.error &&
          typeof resp.open_count === "number" &&
          typeof resp.open_sum === "number"
        ) {
          self.openCountRaw = resp.open_count;
          self.openSumCents = resp.open_sum;
        }
        billingsLoaded = true;
        checkDone();
      })
      .catch(function () {
        billingsLoaded = true;
        checkDone();
      });
  },
};
</script>

<style scoped>
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 0;
  color: #9c9fa6;
}

.loading-container i {
  font-size: 40px;
  margin-bottom: 12px;
}

.kpi-card .card-body {
  text-align: center;
  padding: 20px;
}

.kpi-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 12px;
}

.kpi-icon i {
  font-size: 22px;
  color: #fff;
}

.kpi-value {
  font-size: 28px;
  font-weight: 700;
  color: #1f1f1f;
  line-height: 1.2;
}

.kpi-label {
  font-size: 13px;
  color: #9c9fa6;
  margin-top: 4px;
}

.unclear-title {
  margin-top: 20px;
  font-size: 15px;
}

.unclear-hint {
  font-size: 13px;
  color: #6c7293;
}

.kpi-sub {
  font-size: 13px;
  color: #6c757d;
  margin-top: 2px;
  font-weight: 600;
}

.badge-count {
  background-color: #198ae3;
  color: #fff;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  margin-left: 6px;
  font-weight: 600;
}

.badge-count-warn {
  background-color: #8e44ad;
}

.kpi-row {
  flex-wrap: wrap;
}

.kpi-row > .col {
  min-width: 180px;
}

.link-col {
  color: #0077cc;
  text-decoration: none;
}

.link-col:hover {
  color: #005fa3;
}

.empty-state {
  text-align: center;
  padding: 30px 0;
  color: #9c9fa6;
}
</style>
